/**
 * InstFlowDashboardPage.js
 * 기관수급분석시스템 — 대시보드 (개발계획서 §6.2)
 *
 * C:\Future\inst_flow_radar 의 Python 배치가 Firestore flow_daily/flow_regime_history
 * 에 기록한 데이터를 /api/inst-flow/* 를 통해 조회하여 표시한다.
 *
 * 2026-07-19 기준 KRX 선물옵션 투자자별 순매수/OI/프로그램매매/V-KOSPI는 bld
 * 코드 미확정으로 수집되지 않는다(개발계획서 §2.4) — 해당 카드는 "미수집"으로
 * 표시하고, KIS 선물 시세·VIX 등 확정된 데이터만 실제 값을 보여준다.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Waves, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import apiService from '../../services/apiService';

/* ── 애니메이션 ─────────────────────────────────────────────────────────────── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const spin   = keyframes`from{transform:rotate(0)}to{transform:rotate(360deg)}`;

/* ── 레이아웃 ────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  min-height:100vh;background:#0a0a0f;color:#e2e8f0;
  font-family:'Inter','Pretendard',sans-serif;padding:20px;
`;
const TopBar = styled.div`
  display:flex;align-items:center;gap:12px;
  margin-bottom:24px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;
`;
const BackBtn = styled.button`
  background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;
  &:hover{background:#1e2030;color:#e2e8f0}
`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`
  font-size:22px;font-weight:800;margin:0;
  background:linear-gradient(135deg,#06b6d4,#3b82f6);
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;
`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;

const RefreshBtn = styled.button`
  display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;
  font-size:13px;font-weight:600;cursor:pointer;
  background:rgba(6,182,212,0.1);color:#06b6d4;border:1px solid #06b6d4;
  &:hover{opacity:.8}&:disabled{opacity:.4;cursor:not-allowed}
`;
const SpinIcon = styled(RefreshCw)`
  ${p => p.$spinning && css`animation:${spin} 1s linear infinite;`}
`;

/* ── 국면 배지 ───────────────────────────────────────────────────────────────── */
const REGIME_COLOR = {
  '매집국면': '#10b981',
  '이탈국면': '#ef4444',
  '헤지국면': '#f59e0b',
  '중립':     '#64748b',
};
const RegimeBanner = styled.div`
  display:flex;align-items:center;gap:16px;flex-wrap:wrap;
  background:#111827;border:1px solid #1e2030;border-radius:16px;
  padding:22px 24px;margin-bottom:20px;animation:${fadeIn} .4s ease;
`;
const RegimeBadge = styled.div`
  font-size:20px;font-weight:800;padding:10px 20px;border-radius:12px;
  background:${p => `${p.$color}22`};color:${p => p.$color};border:1px solid ${p => p.$color};
`;
const RegimeScore = styled.div`font-size:14px;color:#94a3b8;`;
const QuadrantTag = styled.span`
  font-size:12px;font-weight:700;padding:4px 10px;border-radius:8px;
  background:rgba(59,130,246,0.12);color:#3b82f6;margin-left:auto;
`;

/* ── 요약 카드 ───────────────────────────────────────────────────────────────── */
const StatGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:12px;
  margin-bottom:24px;animation:${fadeIn} .4s ease;
`;
const StatCard = styled.div`
  background:#111827;border-radius:14px;padding:18px 20px;border:1px solid #1e2030;
`;
const StatLabel = styled.p`margin:0 0 8px;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.06em;`;
const StatVal   = styled.div`
  font-size:22px;font-weight:800;
  color:${p => p.$pos === true ? '#10b981' : p.$pos === false ? '#ef4444' : '#e2e8f0'};
`;
const StatSub = styled.p`margin:4px 0 0;font-size:11px;color:#475569;`;

/* ── 차트 ────────────────────────────────────────────────────────────────────── */
const ChartCard = styled.div`
  background:#111827;border:1px solid #1e2030;border-radius:14px;padding:20px;
  margin-bottom:24px;animation:${fadeIn} .4s ease;
`;
const ChartTitle = styled.h3`margin:0 0 16px;font-size:14px;color:#94a3b8;`;

/* ── 서술 리포트 ─────────────────────────────────────────────────────────────── */
const NarrativeCard = styled.div`
  background:#111827;border:1px solid #1e2030;border-radius:14px;padding:20px;
  animation:${fadeIn} .4s ease;
`;
const NarrativeText = styled.p`
  margin:0;font-size:13px;line-height:1.7;color:#cbd5e1;white-space:pre-line;
`;
const ConfidenceTag = styled.span`
  font-size:11px;color:#64748b;margin-left:8px;
`;

const Empty = styled.div`padding:60px;text-align:center;color:#475569;font-size:13px;`;

/* ── 장중 변곡점 ─────────────────────────────────────────────────────────────── */
const SpikeList = styled.div`display:flex;flex-direction:column;gap:8px;margin-top:14px;`;
const SpikeRow = styled.div`
  display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;
  background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.3);font-size:12px;
`;
const SpikeTime = styled.span`color:#94a3b8;font-weight:600;min-width:48px;`;
const SpikeChange = styled.span`
  font-weight:800;color:${p => p.$up ? '#10b981' : '#ef4444'};
`;
const SpikePrice = styled.span`color:#cbd5e1;`;

const DateInput = styled.input`
  background:#111827;border:1px solid #1e2030;border-radius:8px;
  color:#e2e8f0;font-size:13px;padding:7px 10px;
  &::-webkit-calendar-picker-indicator{filter:invert(.6)}
`;

/* ── 포맷 헬퍼 ───────────────────────────────────────────────────────────────── */
const fmtNumber = (v, suffix = '') => (v == null ? '미수집' : `${v.toLocaleString()}${suffix}`);
const fmtSigned = (v, suffix = '') => (v == null ? '미수집' : `${v >= 0 ? '+' : ''}${v.toLocaleString()}${suffix}`);

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

// YYYYMMDD <-> YYYY-MM-DD (input[type=date] 표현)
const toInputDate = (s) => (s && s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s);
const toCompactDate = (s) => (s ? s.replaceAll('-', '') : s);

// Firestore Timestamp({_seconds}) 또는 ISO 문자열 모두 처리 (RadarDashboardPage와 동일 패턴)
function toDate(ts) {
  if (!ts) return null;
  return ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
}
const fmtHHMM = (ts) => {
  const d = toDate(ts);
  if (!d) return '--:--';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   메인 컴포넌트
═══════════════════════════════════════════════════════════════════════════ */
export default function InstFlowDashboardPage() {
  const navigate = useNavigate();

  const [daily, setDaily] = useState(null);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [intraday, setIntraday] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [selectedDate, setSelectedDate] = useState(todayStr());

  const load = useCallback(async (dateStr) => {
    setLoading(true);
    setError('');
    setNotFound(false);
    try {
      const [historyData, summaryData] = await Promise.all([
        apiService.getInstFlowHistory(60),
        apiService.getInstFlowSummary(),
      ]);
      setHistory(historyData);
      setSummary(summaryData);

      try {
        const dailyData = await apiService.getInstFlowDaily(dateStr);
        setDaily(dailyData);
      } catch (e) {
        setNotFound(true);
        setDaily(null);
      }

      try {
        const intradayData = await apiService.getInstFlowIntraday(dateStr);
        setIntraday(intradayData?.snapshots || []);
      } catch (e) {
        setIntraday([]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(selectedDate); }, [load, selectedDate]);

  const futures = daily?.futures || {};
  const options = daily?.options || {};
  const volIndex = daily?.vol_index || {};
  const regimeColor = REGIME_COLOR[summary?.regime_label] || '#64748b';

  const chartData = history.map(h => ({
    date: `${h.date?.slice(4, 6)}/${h.date?.slice(6, 8)}`,
    score: h.regime_score,
  }));

  const intradaySorted = [...intraday].sort((a, b) => toDate(a.captured_at) - toDate(b.captured_at));
  const intradayChartData = intradaySorted.map(s => ({
    time: fmtHHMM(s.captured_at),
    price: s.price,
    change_pct: s.change_pct,
  }));

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>기관수급분석</PageTitle>
          <PageSub>코스피200 선물/옵션 기관·외국인 수급 · 국면 판정 · 변동성 지표</PageSub>
        </TitleArea>
        <DateInput
          type="date"
          value={toInputDate(selectedDate)}
          max={toInputDate(todayStr())}
          onChange={(e) => setSelectedDate(toCompactDate(e.target.value))}
        />
        <RefreshBtn onClick={() => load(selectedDate)} disabled={loading}>
          <SpinIcon size={14} $spinning={loading} />
          새로고침
        </RefreshBtn>
      </TopBar>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 10,
          padding: '12px 16px', marginBottom: 20, color: '#f87171', fontSize: 13 }}>
          오류: {error} (inst_flow_radar 백엔드/Firestore 연결을 확인하세요)
        </div>
      )}

      <RegimeBanner>
        <RegimeBadge $color={regimeColor}>{summary?.regime_label || '데이터 없음'}</RegimeBadge>
        <RegimeScore>
          {summary?.regime_score != null ? `점수 ${summary.regime_score}/100` : '오늘 배치 미실행'}
        </RegimeScore>
        {summary?.quadrant && <QuadrantTag>{summary.quadrant}</QuadrantTag>}
      </RegimeBanner>

      {notFound && (
        <div style={{ background: 'rgba(100,116,139,0.1)', border: '1px solid #334155', borderRadius: 10,
          padding: '12px 16px', marginBottom: 20, color: '#94a3b8', fontSize: 13 }}>
          {selectedDate === todayStr()
            ? `오늘(${selectedDate}) 배치가 아직 실행되지 않았습니다 — 최근 이력만 표시합니다.`
            : `${selectedDate} 데이터가 없습니다.`}
        </div>
      )}

      <StatGrid>
        <StatCard>
          <StatLabel>외국인 순매수 (선물)</StatLabel>
          <StatVal $pos={futures.foreign_net > 0 ? true : futures.foreign_net < 0 ? false : undefined}>
            {fmtSigned(futures.foreign_net, '계약')}
          </StatVal>
        </StatCard>
        <StatCard>
          <StatLabel>기관 순매수 (선물)</StatLabel>
          <StatVal $pos={futures.inst_net > 0 ? true : futures.inst_net < 0 ? false : undefined}>
            {fmtSigned(futures.inst_net, '계약')}
          </StatVal>
        </StatCard>
        <StatCard>
          <StatLabel>미결제약정(OI) 증감</StatLabel>
          <StatVal $pos={futures.oi_change > 0 ? true : futures.oi_change < 0 ? false : undefined}>
            {fmtSigned(futures.oi_change)}
          </StatVal>
        </StatCard>
        <StatCard>
          <StatLabel>P/C Ratio (1년 백분위)</StatLabel>
          <StatVal>{fmtNumber(options.put_call_ratio_pctile_1y, '%')}</StatVal>
        </StatCard>
        <StatCard>
          <StatLabel>VIX</StatLabel>
          <StatVal>{volIndex.vix != null ? volIndex.vix.toFixed(2) : '미수집'}</StatVal>
        </StatCard>
        <StatCard>
          <StatLabel>V-KOSPI</StatLabel>
          <StatVal>{fmtNumber(volIndex.vkospi)}</StatVal>
        </StatCard>
      </StatGrid>

      <ChartCard>
        <ChartTitle>
          {selectedDate === todayStr() ? '오늘의 장중 변곡점' : `${selectedDate} 장중 변곡점`}
          <ConfidenceTag>(KOSPI200 선물 {intraday[0]?.window_minutes ?? 5}분 급변동 감지)</ConfidenceTag>
        </ChartTitle>
        {intradayChartData.length === 0 ? (
          <Empty>{loading ? '불러오는 중...' : '감지된 장중 변곡점이 없습니다.'}</Empty>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={intradayChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2030" />
                <XAxis dataKey="time" stroke="#64748b" fontSize={11} />
                <YAxis domain={['auto', 'auto']} stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1e2030', borderRadius: 8 }} />
                <Line type="monotone" dataKey="price" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
            <SpikeList>
              {intradaySorted.map((s, i) => (
                <SpikeRow key={s.id || i}>
                  <SpikeTime>{fmtHHMM(s.captured_at)}</SpikeTime>
                  <SpikeChange $up={s.change_pct > 0}>
                    {s.change_pct > 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                    {' '}{s.change_pct > 0 ? '+' : ''}{s.change_pct?.toFixed(2)}%
                  </SpikeChange>
                  <SpikePrice>현재가 {s.price?.toLocaleString()}</SpikePrice>
                </SpikeRow>
              ))}
            </SpikeList>
          </>
        )}
      </ChartCard>

      <ChartCard>
        <ChartTitle>최근 60일 국면 점수 추이</ChartTitle>
        {chartData.length === 0 ? (
          <Empty>{loading ? '불러오는 중...' : '이력 데이터가 없습니다.'}</Empty>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2030" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
              <YAxis domain={[0, 100]} stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1e2030', borderRadius: 8 }} />
              <Line type="monotone" dataKey="score" stroke="#06b6d4" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <NarrativeCard>
        <ChartTitle>
          {selectedDate === todayStr() ? '오늘의 서술 리포트' : `${selectedDate} 서술 리포트`}
          {daily?.narrative?.confidence != null && (
            <ConfidenceTag>(신뢰도 {(daily.narrative.confidence * 100).toFixed(0)}%)</ConfidenceTag>
          )}
        </ChartTitle>
        <NarrativeText>
          {daily?.narrative?.summary_text || '아직 생성된 서술 리포트가 없습니다.'}
        </NarrativeText>
      </NarrativeCard>
    </Page>
  );
}
